import LocationOnIcon from "@mui/icons-material/LocationOn";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import React from "react";

type PlacesService = google.maps.places.PlacesService;
type PlaceResult = google.maps.places.PlaceResult;

type AutocompleteService = google.maps.places.AutocompleteService;
type AutocompletePrediction = google.maps.places.AutocompletePrediction;

const autocompleteService: { current: null | AutocompleteService } = {
  current: null,
};
const placesService: { current: null | PlacesService } = { current: null };

type Props = {
  style?: any;
  variant?: "outlined" | "filled" | "standard";
  // fired when the user selects a location from the list provided by Google's
  // location autocomplete
  onLocationSelected?: (p: AutocompletePrediction | null) => void;
  // fired when we fetch the full Google Place details for the selected locatin
  onPlaceDetails?: (p: PlaceResult | null) => void;
  // fired when the user types in the text input (this is why we have `freeSolo`
  // set to `true`) which allows the user to enter non-physical locations, like
  // a zoom link
  onRawInput?: (input: string) => void;
};

export default function AutocompleteLocation({
  style,
  onLocationSelected,
  onPlaceDetails,
  onRawInput,
  variant = "outlined",
}: Props): React.ReactElement {
  const [value, setValue] = React.useState<AutocompletePrediction | null>(null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<AutocompletePrediction[]>([]);
  const autoCompleteRef = React.useRef(null);

  const fetch = React.useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: AutocompletePrediction[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        200
      ),
    []
  );

  const instantiatePlacesService = () => {
    console.log("trying to instantiate PlacesService", {
      google: (window as any).google,
    });
    if (!placesService.current && (window as any).google) {
      placesService.current = new (
        window as any
      ).google.maps.places.PlacesService(autoCompleteRef.current);
    }
  };

  const fetchPlaceDetails = async (placeId: string) => {
    if (!placesService.current) instantiatePlacesService();
    if (!placesService.current) {
      console.warn("PlacesService is not ready, cannot fetch place details", {
        placeId,
      });
      return;
    } else {
      const request = {
        placeId,
        fields: [
          "name",
          "formatted_address",
          "address_component",
          "photos",
          "place_id",
          "geometry",
          "plus_code",
          "types",
        ],
      };
      console.log("fetching place details", { request });
      await new Promise<void>((resolve) => {
        (placesService.current as any).getDetails(
          request,
          (
            placeResult: PlaceResult | null,
            status: google.maps.places.PlacesServiceStatus
          ) => {
            console.log("placeResult", { placeResult, status });
            if (placeResult && onPlaceDetails)
              onPlaceDetails(placeResult as PlaceResult);
            else {
              console.warn("Unable to fetch place details", {
                request,
                placeResult,
                status,
              });
            }
            resolve();
          }
        );
      });
    }
  };

  // instantiate the PlacesService
  React.useEffect(() => {
    instantiatePlacesService();
  }, []);

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: AutocompletePrediction[]) => {
      if (active) {
        let newOptions = [] as AutocompletePrediction[];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        console.log({ newOptions });
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <>
      <Autocomplete
        freeSolo
        style={style}
        id="google-map"
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        clearOnBlur={false}
        includeInputInList
        filterSelectedOptions
        value={value}
        onChange={(_: any, newValue: AutocompletePrediction | null) => {
          console.log("autocomplete value", { newValue });
          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);
          // fetch
          (newValue && fetchPlaceDetails(newValue.place_id)) ||
            // or reset
            onPlaceDetails?.(null);
          onLocationSelected?.(newValue);
        }}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
          onRawInput?.(newInputValue);
        }}
        renderInput={(params) => (
          <TextField {...params} label="Location" variant={variant} fullWidth />
        )}
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting.main_text_matched_substrings || [];
          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match: any) => [
              match.offset,
              match.offset + match.length,
            ])
          );

          return (
            <li {...props}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <LocationOnIcon />
                </Grid>

                <Grid item xs={12}>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{ fontWeight: part.highlight ? 700 : 400 }}
                    >
                      {part.text}
                    </span>
                  ))}
                  <Typography variant="body2" color="textSecondary">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
      <div ref={autoCompleteRef} />
    </>
  );
}
